// Onboarding wizard API calls.
//
// The wizard holds one property's answers, read from its onboarding profile,
// and the open property is shared by every tab. So each of its writes — a
// step, the analysis it links, the finish — names the property its profile was
// read for (`profile.pmsConnectionId`) in the `connectionId` query parameter,
// and the service refuses it with `ActiveConnectionChangedError` once another
// property is open. A wizard with no profile yet names nothing: the PMS step of
// a new account opens the property itself, in this tab.

import { apiFetch } from "./apiClient";
import {
    loadedConnectionQuery,
    refusedWriteError,
    type LoadedConnectionId,
} from "./loadedConnection";
import { readUnreadableSpecialDateRefusal } from "./specialDateRefusal";

import type {
    OnboardingCompletionMode,
    OnboardingProfile,
    OnboardingStrategyResolution,
    OnboardingStepKey,
    StrategyParamSource,
    StrategyReadiness,
    TypologySource,
    VerifiedAssumptions,
} from "../types/onboarding";

export async function fetchOnboardingStatus(): Promise<{
    completed: boolean;
    profile: OnboardingProfile | null;
}> {
    const res = await apiFetch("/api/onboarding/status");
    if (!res.ok) throw new Error(`fetchOnboardingStatus failed: ${res.status}`);
    const data = await res.json();
    return { completed: data.completed, profile: data.profile };
}

export async function fetchOnboardingHasCompleted(): Promise<boolean> {
    const res = await apiFetch("/api/onboarding/has-completed");
    if (!res.ok) throw new Error(`fetchOnboardingHasCompleted failed: ${res.status}`);
    const data = await res.json();
    return data.hasCompleted === true;
}

export async function startOnboarding(): Promise<OnboardingProfile> {
    const res = await apiFetch("/api/onboarding/start", { method: "POST" });
    if (!res.ok) throw new Error(`startOnboarding failed: ${res.status}`);
    const data = await res.json();
    return data.profile;
}

export interface SaveOnboardingProgress {
    stepKey?: OnboardingStepKey;
    completedStepKey?: OnboardingStepKey;
    currentQuestionId?: string | null;
    typologySource?: TypologySource;
}

/**
 * Save a step's answers onto the open property's profile. `loadedConnectionId`
 * names the property the wizard's profile was read for, and the service
 * refuses the save with `ActiveConnectionChangedError` once another property
 * is open; undefined names nothing.
 */
export async function saveOnboardingStep(
    step: number,
    assumptions?: Partial<VerifiedAssumptions>,
    rawDerivedSnapshot?: Record<string, unknown>,
    progress: SaveOnboardingProgress = {},
    loadedConnectionId?: LoadedConnectionId,
): Promise<OnboardingProfile> {
    const res = await apiFetch(`/api/onboarding/save-step${loadedConnectionQuery(loadedConnectionId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ step, assumptions, rawDerivedSnapshot, ...progress }),
    });
    // A special date the engine could never match: the wizard stays on the step.
    const refusal = await readUnreadableSpecialDateRefusal(res);
    if (refusal) throw refusal;
    if (res.status === 409) throw await refusedWriteError(res, "saveOnboardingStep failed");
    if (!res.ok) throw new Error(`saveOnboardingStep failed: ${res.status}`);
    const data = await res.json();
    return data.profile;
}

export async function previewOnboardingSettings(
    assumptions?: Partial<VerifiedAssumptions>,
    rawDerivedSnapshot?: Record<string, unknown>,
): Promise<OnboardingStrategyResolution> {
    const res = await apiFetch("/api/onboarding/preview-settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assumptions, rawDerivedSnapshot }),
    });
    if (!res.ok) throw new Error(`previewOnboardingSettings failed: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to preview onboarding settings");
    return data.resolution;
}

export interface DeriveFromAnswersResult {
    params: Record<string, unknown>;
    fieldSources: Record<string, StrategyParamSource>;
    fieldQuestions: Record<string, string[]>;
}

/**
 * Re-derive strategy params after a section answer changes in the editor.
 * Stateless — the caller decides whether to save the returned params.
 */
export async function deriveParamsFromAnswers(payload: {
    answers: Record<string, string[]>;
    currentParams: Record<string, unknown>;
    fieldSources: Record<string, StrategyParamSource>;
    /**
     * Every room kind the caller knows about. The params maps name only the
     * room kinds something has already priced or configured, which leaves out
     * exactly the ones the expected prices are meant to prefill.
     */
    roomKindIds?: readonly string[];
}): Promise<DeriveFromAnswersResult> {
    const res = await apiFetch("/api/onboarding/derive-from-answers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`deriveParamsFromAnswers failed: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to apply the answer");
    return data.result;
}

/** Link a completed analysis to the open property's profile, named as a step's save is. */
export async function linkOnboardingAnalysis(
    analysisId: number,
    rawSnapshot: Record<string, unknown>,
    loadedConnectionId?: LoadedConnectionId,
): Promise<OnboardingProfile> {
    const res = await apiFetch(`/api/onboarding/link-analysis${loadedConnectionQuery(loadedConnectionId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ analysisId, rawSnapshot }),
    });
    if (res.status === 409) throw await refusedWriteError(res, "linkOnboardingAnalysis failed");
    if (!res.ok) throw new Error(`linkOnboardingAnalysis failed: ${res.status}`);
    const data = await res.json();
    return data.profile;
}

export interface FinalizeOnboardingOptions {
    strategyName?: string;
    mode?: OnboardingCompletionMode;
    completionStepKey?: OnboardingStepKey;
    typologySource?: TypologySource;
}

export interface FinalizeOnboardingResult {
    strategyId: string;
    profile: OnboardingProfile;
    readiness: StrategyReadiness;
    draftGeneration: "started" | "skipped_missing_base_price" | "already_completed";
}

/**
 * Finish the wizard for the property `loadedConnectionId` names: the strategy
 * it seeds switches off whichever one was pricing that property, so a wizard
 * loaded for another than the one open now is refused rather than finished
 * there.
 */
export async function finalizeOnboarding(
    options: FinalizeOnboardingOptions = {},
    loadedConnectionId?: LoadedConnectionId,
): Promise<FinalizeOnboardingResult> {
    const res = await apiFetch(`/api/onboarding/finalize${loadedConnectionQuery(loadedConnectionId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(options),
    });
    if (res.status === 409) throw await refusedWriteError(res, "finalizeOnboarding failed");
    if (!res.ok) throw new Error(`finalizeOnboarding failed: ${res.status}`);
    return res.json();
}

export async function resetOnboarding(): Promise<OnboardingProfile> {
    const res = await apiFetch("/api/onboarding/reset", { method: "POST" });
    if (!res.ok) throw new Error(`resetOnboarding failed: ${res.status}`);
    const data = await res.json();
    return data.profile;
}

export async function detectPropertyCountry(): Promise<string | null> {
    const res = await apiFetch("/api/onboarding/detect-country");
    if (!res.ok) return null;
    const data = await res.json();
    return data.countryCode ?? null;
}

export async function fetchHolidays(
    country: string,
    year: number,
): Promise<{ date: string; name: string; type: string }[]> {
    const res = await apiFetch(`/api/onboarding/holidays?country=${encodeURIComponent(country)}&year=${year}`);
    if (!res.ok) return [];
    const data = await res.json();
    return data.holidays ?? [];
}
